import React, { FC, useState } from 'react';
import * as yup from 'yup';
import { updatePassword } from 'aws-amplify/auth';
import { Heading, List, Text } from '@chakra-ui/react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { passwordRegExp } from '@/libs/utils';
import { PasswordInput } from '@/components/ui/password-input';
import { Alert } from '../ui/alert';
import { Field } from '../ui/field';
import { Button } from '../ui/button';

type NewPasswordFormProps = {
  callback(err: string | null, password?: string): void;
};

const NewPasswordForm: FC<NewPasswordFormProps> = ({ callback }) => {
  const [error, setError] = useState('');
  const newPasswordSchema = yup.object({
    password: yup
      .string()
      .matches(passwordRegExp, {
        message: (cur) => {
          const fails: string[] = [];
          if (!cur.value.match(/[a-z]/)) {
            fails.push('lowercase');
          }
          if (!cur.value.match(/[A-Z]/)) {
            fails.push('uppercase');
          }
          if (!cur.value.match(/[0-9]/)) {
            fails.push('number');
          }
          if (!cur.value.match(/[!@#$%^&*\-_.]/)) {
            fails.push('special character');
          }
          if (cur.value.match(/^\s/)) {
            fails.push('no spaces at the start or end');
          }

          return `Password must include ${fails.join(', ')}`;
        }
      })
      .min(8, 'Minimum length is 8 characters')
      .required('New password is required'),
    confirmPassword: yup
      .string()
      .oneOf([yup.ref('password')], 'Passwords must match')
      .required('Confirm password is required')
  });
  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting }
  } = useForm({
    resolver: yupResolver(newPasswordSchema),
    defaultValues: {
      password: ''
    }
  });

  const onSubmit = async (evt: any) => {
    const { password } = evt;
    setError('');

    try {
      await updatePassword({ oldPassword: password, newPassword: password });

      callback(null);
    } catch (err: any) {
      console.error({ action: 'newPassword', error: err });
      setError(err?.message);
    }
  };

  return (
    <>
      <Heading as='h3'>Password change required</Heading>
      <form onSubmit={handleSubmit(onSubmit)}>
        {/* Errors */}
        {error.length > 0 && (
          <Alert status='error' title='Sign-up error' colorPalette='brand'>
            {error}
          </Alert>
        )}

        <Controller
          name='password'
          control={control}
          render={({ field }) => (
            <Field
              required
              invalid={!!errors.password}
              label='New Password'
              errorText={errors.password?.message}
              helperText={
                <>
                  <Text fontSize='xs'>Your password should contain the following:</Text>
                  <List.Root as='ol' px='4' pt='2'>
                    <List.Item>minimum length of 8 characters</List.Item>
                    <List.Item>a numerical letter e.g. 123</List.Item>
                    <List.Item>a capital letter e.g. ABC</List.Item>
                    <List.Item>a lower case letter e.g. abc</List.Item>
                    <List.Item>a special character e.g. !@#$</List.Item>
                  </List.Root>
                </>
              }
            >
              <PasswordInput {...field} placeholder='New password' />
              {/* <InputGroup size='md'>
                <Input
                  {...field}
                  id='password'
                  autoComplete='new-password'
                  placeholder='New password'
                  pr='4.5rem'
                  type={showPassword ? 'text' : 'password'}
                />
                <InputRightElement width='4.5rem' mt='1' mr='3'>
                  <Button h='1.75rem' size='sm' onClick={toggleShowPassword}>
                    {showPassword ? 'Hide' : 'Show'}
                  </Button>
                </InputRightElement>
              </InputGroup> */}
            </Field>
          )}
        />

        <Button type='submit' mt='8' w='full' loading={isSubmitting} disabled={!!errors.password}>
          Change password
        </Button>
      </form>
    </>
  );
};

export default NewPasswordForm;
